import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { useApi } from '~/contexts';
import { colors } from '~/styles';
import {
  Grid,
  Card,
  CardLabel,
  CardValue,
  CardSubValue,
  Section,
  SectionTitle,
  ChartLabel,
  ChartWrapper,
} from './shared';

function ConversationDepth({ start, end }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getConversationDepth({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  if (isLoading || !data) return null;

  return (
    <Section>
      <SectionTitle>Conversations &amp; Messages</SectionTitle>
      <Grid>
        <Card>
          <CardLabel>Total Conversations</CardLabel>
          <CardValue>{data.totalConversations.toLocaleString()}</CardValue>
          <CardSubValue>In the selected time period</CardSubValue>
        </Card>
        <Card>
          <CardLabel>Avg Turns per Conversation</CardLabel>
          <CardValue>{data.avgTurns.toFixed(1)}</CardValue>
          <CardSubValue>User messages per conversation</CardSubValue>
        </Card>
        <Card>
          <CardLabel>Max Turns</CardLabel>
          <CardValue>{data.maxTurns}</CardValue>
          <CardSubValue>Deepest conversation in the period</CardSubValue>
        </Card>
      </Grid>
    </Section>
  );
}

function ConversationTurnDistribution({ start, end }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getConversationDepth({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  if (isLoading || !data) return null;

  return <TurnDistribution data={data} />;
}

function TurnDistribution({ data }) {
  const chartData = useMemo(() => {
    if (!data?.distribution) return null;
    return {
      labels: data.distribution.map((d) => d.label),
      datasets: [
        {
          data: data.distribution.map((d) => d.count),
          backgroundColor: colors.primary,
        },
      ],
    };
  }, [data]);

  if (!chartData) return null;

  return (
    <Section>
      <ChartLabel>Turn Distribution</ChartLabel>
      <ChartWrapper>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: { y: { ticks: { precision: 0 } } },
          }}
        />
      </ChartWrapper>
    </Section>
  );
}

function ActivityOverTime({ start, end, granularity = 'day' }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getActivityOverTime({ start, end, granularity });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, granularity]);

  const formatMap = { day: 'MMM D', week: 'MMM D', month: 'MMM YYYY' };
  const dateFormat = formatMap[granularity] || 'MMM D';
  const labelMap = { day: 'Daily', week: 'Weekly', month: 'Monthly' };
  const granularityLabel = labelMap[granularity] || 'Daily';

  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.map((d) => moment.utc(d.date).format(dateFormat)),
      datasets: [
        {
          label: 'Conversations',
          order: 1,
          data: data.map((d) => d.conversations),
          backgroundColor: colors.grey25,
        },
        {
          label: 'User Messages',
          order: 1,
          data: data.map((d) => d.userMessages),
          backgroundColor: colors.primary,
        },
        {
          label: 'Msgs / Conversation',
          type: 'line',
          yAxisID: 'y1',
          order: 0,
          data: data.map((d) => (d.conversations > 0 ? +(d.userMessages / d.conversations).toFixed(1) : 0)),
          borderColor: colors.warning,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          pointHitRadius: 6,
          tension: 0.3,
        },
      ],
    };
  }, [data, dateFormat]);

  if (isLoading || !data) return null;

  return (
    <Section>
      <ChartLabel>Conversations &amp; Messages ({granularityLabel})</ChartLabel>
      <ChartWrapper>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index' },
            plugins: { legend: { display: true } },
            scales: {
              y: { position: 'left', ticks: { precision: 0 }, beginAtZero: true },
              y1: {
                position: 'right',
                title: { display: true, text: 'Msgs / Conversation' },
                beginAtZero: true,
                grid: { drawOnChartArea: false },
              },
            },
          }}
        />
      </ChartWrapper>
    </Section>
  );
}

function MissedMessages({ start, end, granularity = 'day' }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const [missedRes, activityRes] = await Promise.all([
          api.admin.aiDashboard.getMissedMessages({ start, end, granularity }),
          api.admin.aiDashboard.getActivityOverTime({ start, end, granularity }),
        ]);
        if (!cancelled) {
          const activityMap = {};
          for (const row of activityRes.data) {
            activityMap[moment.utc(row.date).format('YYYY-MM-DD')] = row.userMessages;
          }
          setData(
            missedRes.data.map((d) => {
              const date = moment.utc(d.date).format('YYYY-MM-DD');
              const totalMessages = activityMap[date] || 0;
              return {
                ...d,
                missedRate: totalMessages > 0 ? +((d.missedMessages / totalMessages) * 100).toFixed(1) : 0,
              };
            }),
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, granularity]);

  const formatMap = { day: 'MMM D', week: 'MMM D', month: 'MMM YYYY' };
  const dateFormat = formatMap[granularity] || 'MMM D';
  const labelMap = { day: 'Daily', week: 'Weekly', month: 'Monthly' };
  const granularityLabel = labelMap[granularity] || 'Daily';

  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.map((d) => moment.utc(d.date).format(dateFormat)),
      datasets: [
        {
          label: 'Missed Messages',
          order: 1,
          data: data.map((d) => d.missedMessages),
          backgroundColor: colors.danger,
        },
        {
          label: 'Missed %',
          type: 'line',
          yAxisID: 'y1',
          order: 0,
          data: data.map((d) => d.missedRate),
          borderColor: colors.warning,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          pointHitRadius: 6,
          tension: 0.3,
        },
      ],
    };
  }, [data, dateFormat]);

  if (isLoading || !data) return null;

  return (
    <Section>
      <ChartLabel>Missed Messages ({granularityLabel})</ChartLabel>
      <ChartWrapper>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index' },
            plugins: {
              legend: { display: true },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    if (context.dataset.yAxisID === 'y1') return `${context.dataset.label}: ${context.raw}%`;
                    return `${context.dataset.label}: ${context.raw}`;
                  },
                },
              },
            },
            scales: {
              y: { position: 'left', ticks: { precision: 0 }, beginAtZero: true },
              y1: {
                position: 'right',
                title: { display: true, text: 'Missed %' },
                beginAtZero: true,
                grid: { drawOnChartArea: false },
              },
            },
          }}
        />
      </ChartWrapper>
    </Section>
  );
}

function FeedbackOverTime({ start, end, granularity = 'day' }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getFeedbackOverTime({ start, end, granularity });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, granularity]);

  const formatMap = { day: 'MMM D', week: 'MMM D', month: 'MMM YYYY' };
  const dateFormat = formatMap[granularity] || 'MMM D';
  const labelMap = { day: 'Daily', week: 'Weekly', month: 'Monthly' };
  const granularityLabel = labelMap[granularity] || 'Daily';

  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.map((d) => moment.utc(d.date).format(dateFormat)),
      datasets: [
        {
          label: 'Thumbs Up',
          data: data.map((d) => d.thumbsUp),
          backgroundColor: colors.primary,
        },
        {
          label: 'Thumbs Down',
          data: data.map((d) => d.thumbsDown),
          backgroundColor: colors.danger,
        },
      ],
    };
  }, [data, dateFormat]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' },
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          footer: (items) => {
            const total = items.reduce((sum, item) => sum + item.raw, 0);
            return `Total: ${total}`;
          },
        },
      },
    },
    scales: { x: { stacked: true }, y: { stacked: true, ticks: { precision: 0 }, beginAtZero: true } },
  };

  if (isLoading || !data) return null;

  return (
    <Section>
      <ChartLabel>Feedback ({granularityLabel})</ChartLabel>
      <ChartWrapper>
        <Bar data={chartData} options={barOptions} />
      </ChartWrapper>
    </Section>
  );
}

function Feedback({ start, end, granularity }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getFeedback({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  if (isLoading || !data) return null;

  return (
    <>
      <Section>
        <SectionTitle>Feedback</SectionTitle>
        <Grid>
          <Card>
            <CardLabel>Positive Rate</CardLabel>
            <CardValue>{(data.positiveRate * 100).toFixed(0)}%</CardValue>
            <CardSubValue>
              <strong>{data.thumbsUp}</strong> thumbs up of <strong>{data.total}</strong> rated responses
            </CardSubValue>
          </Card>
          <Card>
            <CardLabel>Thumbs Up</CardLabel>
            <CardValue>{data.thumbsUp}</CardValue>
          </Card>
          <Card>
            <CardLabel>Thumbs Down</CardLabel>
            <CardValue>{data.thumbsDown}</CardValue>
          </Card>
        </Grid>
      </Section>
      <FeedbackOverTime start={start} end={end} granularity={granularity} />
    </>
  );
}

export default function Conversations({ start, end, granularity }) {
  return (
    <>
      <ConversationDepth start={start} end={end} />
      <ActivityOverTime start={start} end={end} granularity={granularity} />
      <MissedMessages start={start} end={end} granularity={granularity} />
      <ConversationTurnDistribution start={start} end={end} />
      <Feedback start={start} end={end} granularity={granularity} />
    </>
  );
}
